import { useState } from "react"
import { Eye } from "lucide-react"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import type { DraftDetail } from "./types"
import { OriginalMailPopup } from "./ActionPlanCard"
import { AttachmentsSection } from "@/components/portal/AttachmentsSection"
import { ProaktivHerkunft } from "./proaktiv"

/**
 * Linke Seite der Review-Card (40% Breite).
 * Zeigt Kontext-Informationen in strukturierten Abschnitten.
 */
export function ContextPanel({ detail }: { detail: DraftDetail }) {
  const [mailPopupOpen, setMailPopupOpen] = useState(false)

  return (
    <div style={{
      background: "#F5F4F0", padding: 20, borderRadius: 8,
      display: "flex", flexDirection: "column", gap: 18,
      fontSize: 13, color: "#2C2C2A", overflowY: "auto",
    }}>
      <Section label="Von">
        <div style={{ fontWeight: 500 }} data-pii>{detail.sender || detail.sender_email || "—"}</div>
        {detail.customer_status && (
          <div style={{ fontSize: 12, color: "#5F5E5A", marginTop: 2 }}>{detail.customer_status}</div>
        )}
      </Section>

      {detail.is_proactive ? <ProaktivHerkunft detail={detail} /> : (
      <div>
        <SectionLabel>Original-Mail</SectionLabel>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {detail.original_excerpt ? (
              <div data-pii style={{
                fontStyle: "italic", color: "#5F5E5A", lineHeight: 1.5,
                fontSize: 12, whiteSpace: "pre-wrap", overflow: "hidden",
                display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
              }}>
                „{detail.original_excerpt}"
              </div>
            ) : (
              <div style={{ fontStyle: "italic", color: "#888780", fontSize: 12 }}>
                Kein lesbarer Text (vermutlich Bild-Mail oder leerer Body).
              </div>
            )}
          </div>
          <button type="button" onClick={() => setMailPopupOpen(true)}
            title="Original-Mail vollstaendig anzeigen"
            style={{
              background: "#fff", border: "0.5px solid rgba(44,44,42,0.18)",
              padding: "3px 8px", borderRadius: 5, fontSize: 12, color: "#3C3489",
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
              flexShrink: 0,
            }}>
            <Eye size={11} /> Anzeigen
          </button>
        </div>
      </div>
      )}
      {mailPopupOpen && (
        <OriginalMailPopup detail={detail} onClose={() => setMailPopupOpen(false)} />
      )}

      {/* Portal-Eintraege sind keine Mails — es gibt nichts anzuhaengen, und der
          Endpunkt antwortet mit 404 (dev, 06.09.2026: „Laedt Anhaenge…" blieb stehen). */}
      {!detail.is_proactive && <AttachmentsSection emailId={detail.email_id} tenantSlug={detail.tenant_slug} />}

      {detail.considered.length > 0 && (
        <Section label="Claude hat beruecksichtigt">
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {detail.considered.map((c, i) => (
              <li key={i} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 12 }}>✓</span>
                <span>
                  <span style={{ fontWeight: 500 }}>{c.label}:</span>{" "}
                  <span style={{ color: "#5F5E5A" }} data-pii>{c.value}</span>
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {detail.hero_action && (
        <Section label="HERO-Aktion">
          <div style={{ fontSize: 12 }}>
            {detail.hero_action.project_id && (
              <div>
                <HeroVorgangName
                  tenantSlug={detail.tenant_slug}
                  projectId={detail.hero_action.project_id}
                  bekannt={detail.hero_action.project_title}
                />
                {detail.hero_action.new_status && <> · Status → „{detail.hero_action.new_status}"</>}
              </div>
            )}
          </div>
        </Section>
      )}
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5,
      color: "#888780", marginBottom: 6, fontWeight: 500,
    }}>
      {children}
    </div>
  )
}
